/**
 * PageZoomLightbox — reusable click-to-zoom modal for page-image viewers.
 *
 * Mirrors the UX of the snapshot viewer's original internal ``ZoomLightbox``
 * (zoom 0.5–5× in ±0.5 steps, a "Page N · M/total" counter, Prev/Next, Close,
 * scrollable when zoomed) but is corpus-agnostic: instead of baking in the page
 * image + mark math, it takes a ``renderPage(index)`` render-prop that returns a
 * fully-overlaid page node (the page IMAGE plus its percentage/normalised-
 * positioned highlight overlays).
 *
 * Why a render-prop and not props for image/marks?  The live compare viewers
 * (``PageRenderer`` for SBA/EPAR/CA/AU/UK, ``PageImage`` inside
 * ``FdaLetterPageViewer`` for FDA letters) already render each page as a single
 * relative-positioned container whose children are the keyword ``<mark>`` term
 * overlays and the ChunkHighlight / bbox→pixel rectangle, all positioned in
 * PERCENTAGES of that container.  By re-rendering the exact same node here and
 * applying the zoom as a width transform on a wrapper, every overlay scales WITH
 * the page and stays pixel-aligned at any zoom level — no highlight math is
 * re-implemented or duplicated.
 *
 * Used by:
 *  - ``SnapshotPdfViewer`` (public snapshot pages)
 *  - ``RegulatoryViewerEmbed`` (live SBA/EPAR/CA/AU/UK compare panels)
 *  - ``FdaLetterPageViewer`` (live FDA-letter compare panels)
 */
import { useState } from 'react';

export interface PageZoomLightboxProps {
  /** Total number of pages the user can Prev/Next through. */
  pageCount: number;
  /** Currently shown page index (0-based). */
  index: number;
  /** Move to another page index (clamped by the caller's bounds). */
  onIndex: (i: number) => void;
  /** Close the lightbox. */
  onClose: () => void;
  /**
   * Render the fully-overlaid page at ``index``: the page image PLUS its
   * keyword ``<mark>`` term overlays and chunk-highlight bbox, all as
   * percentage-positioned children of ONE relative container. The lightbox
   * scales that container's width to zoom, so the overlays scale with it and
   * stay aligned. Should fill width (e.g. ``className="block w-full"`` on the
   * container) so the zoom transform behaves.
   */
  renderPage: (index: number) => React.ReactNode;
  /**
   * Optional human label for the counter (e.g. "Page 12"). Defaults to
   * ``Page {index + 1}``. FDA-letter page_num is 0-based, so that viewer
   * passes ``index + 1`` explicitly via this hook.
   */
  pageLabel?: (index: number) => string;
}

export default function PageZoomLightbox({
  pageCount,
  index,
  onIndex,
  onClose,
  renderPage,
  pageLabel,
}: PageZoomLightboxProps) {
  const [scale, setScale] = useState(1.5);
  const label = pageLabel ? pageLabel(index) : `Page ${index + 1}`;
  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-black/80" onClick={onClose}>
      <div className="flex items-center justify-between gap-2 px-4 py-2 text-white" onClick={(e) => e.stopPropagation()}>
        <span className="text-sm">{label} · {index + 1}/{pageCount}</span>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setScale((s) => Math.max(0.5, s - 0.5))} className="rounded bg-white/15 px-2.5 py-1 text-sm hover:bg-white/25">−</button>
          <span className="w-12 text-center text-xs tabular-nums">{Math.round(scale * 100)}%</span>
          <button onClick={() => setScale((s) => Math.min(5, s + 0.5))} className="rounded bg-white/15 px-2.5 py-1 text-sm hover:bg-white/25">+</button>
          <button onClick={() => onIndex(Math.max(0, index - 1))} disabled={index === 0} className="ml-2 rounded bg-white/15 px-2.5 py-1 text-sm hover:bg-white/25 disabled:opacity-40">‹ Prev</button>
          <button onClick={() => onIndex(Math.min(pageCount - 1, index + 1))} disabled={index === pageCount - 1} className="rounded bg-white/15 px-2.5 py-1 text-sm hover:bg-white/25 disabled:opacity-40">Next ›</button>
          <button onClick={onClose} className="ml-2 rounded bg-white/15 px-2.5 py-1 text-sm hover:bg-white/25">✕ Close</button>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
        {/* Single zoom container: width drives the scale, so the page image and
            its percentage-positioned overlay children scale together. */}
        <div className="mx-auto" style={{ width: `${scale * 100}%` }}>
          {renderPage(index)}
        </div>
      </div>
    </div>
  );
}
